/**
 * Learning Plans service for Firebase
 * Handles user learning plans, course likes/dislikes, and dashboard statistics
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  setDoc,
  query,
  where,
  limit,
  Timestamp,
  Unsubscribe,
} from 'firebase/firestore';
import { db, auth } from './config';
import { subscribeToDocument } from './firestore';
import { progressService } from './progress';
import { UserProgress } from '../../types/course';

/**
 * Interface for liked courses in learning plans
 */
export interface LikedCourse {
  courseId: string;
  courseTitle: string;
  addedAt: Timestamp;
  startedAt?: Timestamp;
  completedAt?: Timestamp;
  lastAccessedAt?: Timestamp;
  progress: number;
  timeSpent: number;
  priority: number;
  source: 'recommendation' | 'catalog' | 'manual';
  status: 'active' | 'completed' | 'paused';
}

/**
 * Interface for user learning plans
 */
export interface UserLearningPlan {
  id: string;
  userId: string;
  planId?: string;
  assessmentId?: string;
  name: string;
  description?: string;
  status: 'active' | 'paused' | 'completed';
  likedCourses: LikedCourse[];
  totalCourses: number;
  completedCourses: number;
  totalLearningTime: number;
  estimatedDuration: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  lastAccessedAt: Timestamp;
}

/**
 * Interface for course feedback
 */
export interface CourseFeedback {
  id: string;
  userId: string;
  courseId: string;
  feedback: 'like' | 'dislike';
  source: 'recommendation' | 'catalog' | 'manual';
  context?: string;
  userProfile?: {
    currentRole?: string;
    industry?: string;
    experienceLevel?: number;
  };
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Interface for dashboard statistics
 */
export interface UserDashboardStats {
  id: string;
  userId: string;
  coursesInProgress: number;
  coursesCompleted: number;
  totalCoursesEnrolled: number;
  totalLearningTime: number;
  thisWeekLearningTime: number;
  thisMonthLearningTime: number;
  currentStreak: number;
  longestStreak: number;
  lastLearningDate: Timestamp;
  certificatesEarned: number;
  badgesEarned: number;
  lastCalculated: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Learning Plans service class
 */
export class LearningPlansService {
  private static instance: LearningPlansService;
  
  public static getInstance(): LearningPlansService {
    if (!LearningPlansService.instance) {
      LearningPlansService.instance = new LearningPlansService();
    }
    return LearningPlansService.instance;
  }

  /**
   * Get user's learning plan
   */
  async getUserLearningPlan(): Promise<UserLearningPlan | null> {
    try {
      if (!auth.currentUser) return null;
      
      const userId = auth.currentUser.uid;
      const q = query(
        collection(db, 'user_learning_plans'),
        where('userId', '==', userId),
        where('status', '==', 'active'),
        limit(1)
      );
      
      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;
      
      const doc = snapshot.docs[0];
      const data = doc.data();
      
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        lastAccessedAt: data.lastAccessedAt,
      } as UserLearningPlan;
    } catch (error) {
      console.error('Error getting user learning plan:', error);
      return null;
    }
  }

  /**
   * Create a new learning plan for user
   */
  async createUserLearningPlan(
    name: string = 'My AI Learning Journey',
    description?: string,
    planId?: string,
    assessmentId?: string
  ): Promise<string> {
    try {
      if (!auth.currentUser) throw new Error('User not authenticated');
      
      const userId = auth.currentUser.uid;
      const now = Timestamp.now();

      // Archive any existing active plans
      const activePlan = await this.getUserLearningPlan();
      if (activePlan) {
        await updateDoc(doc(db, 'user_learning_plans', activePlan.id), {
          status: 'completed',
          updatedAt: now,
        });
      }
      
      const learningPlan: Omit<UserLearningPlan, 'id'> = {
        userId,
        name,
        status: 'active',
        likedCourses: [],
        totalCourses: 0,
        completedCourses: 0,
        totalLearningTime: 0,
        estimatedDuration: 0,
        createdAt: now,
        updatedAt: now,
        lastAccessedAt: now,
      };

      // Only add optional fields if they have values
      if (planId) {
        learningPlan.planId = planId;
      }
      if (assessmentId) {
        learningPlan.assessmentId = assessmentId;
      }
      if (description) {
        learningPlan.description = description;
      }
      
      const docRef = await addDoc(collection(db, 'user_learning_plans'), learningPlan);
      return docRef.id;
    } catch (error) {
      console.error('Error creating learning plan:', error);
      throw error;
    }
  }

  /**
   * Add course to user's learning plan (thumbs up)
   */
  async addCourseToLearningPlan(
    courseId: string,
    courseTitle: string,
    source: 'recommendation' | 'catalog' | 'manual' = 'recommendation',
    priority: number = 3
  ): Promise<void> {
    try {
      if (!auth.currentUser) throw new Error('User not authenticated');
      
      let learningPlan = await this.getUserLearningPlan();
      
      // Create learning plan if it doesn't exist
      if (!learningPlan) {
        await this.createUserLearningPlan();
        learningPlan = await this.getUserLearningPlan();
        if (!learningPlan) throw new Error('Failed to create learning plan');
      }
      
      // Check if course is already in the plan
      const existingCourse = learningPlan.likedCourses.find(c => c.courseId === courseId);
      if (existingCourse) {
        return;
      }
      
      const now = Timestamp.now();
      const newCourse: LikedCourse = {
        courseId,
        courseTitle,
        addedAt: now,
        progress: 0,
        timeSpent: 0,
        priority,
        source,
        status: 'active', // Set initial status
      };
      
      const updatedCourses = [...learningPlan.likedCourses, newCourse];
      
      await updateDoc(doc(db, 'user_learning_plans', learningPlan.id), {
        likedCourses: updatedCourses,
        totalCourses: updatedCourses.length,
        updatedAt: now,
        lastAccessedAt: now,
      });
      
      // Record positive feedback
      await this.recordCourseFeedback(courseId, 'like', source);
      
      // Recalculate dashboard statistics
      await this.calculateDashboardStats();
      
    } catch (error) {
      console.error('Error adding course to learning plan:', error);
      throw error;
    }
  }

  /**
   * Remove course from user's learning plan (thumbs down)
   */
  async removeCourseFromLearningPlan(courseId: string, source: 'recommendation' | 'catalog' = 'recommendation'): Promise<void> {
    try {
      if (!auth.currentUser) throw new Error('User not authenticated');
      
      const learningPlan = await this.getUserLearningPlan();
      if (!learningPlan) return;
      
      const updatedCourses = learningPlan.likedCourses.filter(c => c.courseId !== courseId);
      const now = Timestamp.now();
      
      await updateDoc(doc(db, 'user_learning_plans', learningPlan.id), {
        likedCourses: updatedCourses,
        totalCourses: updatedCourses.length,
        updatedAt: now,
      });
      
      // Record negative feedback
      await this.recordCourseFeedback(courseId, 'dislike', source);
      
      // Recalculate dashboard statistics
      await this.calculateDashboardStats();
      
    } catch (error) {
      console.error('Error removing course from learning plan:', error);
      throw error;
    }
  }

  /**
   * Record course feedback for analytics
   */
  async recordCourseFeedback(
    courseId: string,
    feedback: 'like' | 'dislike',
    source: 'recommendation' | 'catalog' | 'manual',
    context?: string
  ): Promise<void> {
    try {
      if (!auth.currentUser) return;
      
      const userId = auth.currentUser.uid;
      const now = Timestamp.now();
      
      // Check if feedback already exists
      const q = query(
        collection(db, 'course_feedback'),
        where('userId', '==', userId),
        where('courseId', '==', courseId),
        where('source', '==', source)
      );
      
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        // Update existing feedback
        const existingDoc = snapshot.docs[0];
        await updateDoc(existingDoc.ref, {
          feedback,
          updatedAt: now,
        });
      } else {
        // Create new feedback record
        const feedbackData: Omit<CourseFeedback, 'id'> = {
          userId,
          courseId,
          feedback,
          source,
          createdAt: now,
          updatedAt: now,
        };

        // Only add context if it has a value
        if (context) {
          feedbackData.context = context;
        }
        
        await addDoc(collection(db, 'course_feedback'), feedbackData);
      }
    } catch (error) {
      console.error('Error recording course feedback:', error);
    }
  }

  /**
   * Update course progress in learning plan
   */
  async updateCourseProgress(
    courseId: string,
    progress: number,
    timeSpent: number,
    isCompleted: boolean = false
  ): Promise<void> {
    try {
      if (!auth.currentUser) return;
      
      const learningPlan = await this.getUserLearningPlan();
      if (!learningPlan) return;
      
      const courseIndex = learningPlan.likedCourses.findIndex(c => c.courseId === courseId);
      if (courseIndex === -1) return;
      
      const now = Timestamp.now();
      const updatedCourses = [...learningPlan.likedCourses];
      const course = updatedCourses[courseIndex];
      
      // Update course progress
      course.progress = progress;
      course.timeSpent = timeSpent;
      
      if (isCompleted && !course.completedAt) {
        course.completedAt = now;
      }
      
      if (!course.startedAt && progress > 0) {
        course.startedAt = now;
      }
      
      // Calculate completed courses count
      const completedCount = updatedCourses.filter(c => c.completedAt).length;
      const totalTime = updatedCourses.reduce((sum, c) => sum + c.timeSpent, 0);
      
      await updateDoc(doc(db, 'user_learning_plans', learningPlan.id), {
        likedCourses: updatedCourses,
        completedCourses: completedCount,
        totalLearningTime: totalTime,
        updatedAt: now,
        lastAccessedAt: now,
      });
      
      // Update dashboard stats
      await this.updateDashboardStats();
      
    } catch (error) {
      console.error('Error updating course progress:', error);
      throw error;
    }
  }

  /**
   * Get user's dashboard statistics
   */
  async getDashboardStats(): Promise<UserDashboardStats | null> {
    try {
      if (!auth.currentUser) return null;

      const userId = auth.currentUser.uid;
      const statsDoc = await getDoc(doc(db, 'user_dashboard_stats', userId));
      
      if (!statsDoc.exists()) {
        // Calculate and create initial stats
        await this.calculateDashboardStats();
        return this.getDashboardStats();
      }
      
      const data = statsDoc.data();
      return {
        id: statsDoc.id,
        ...data,
        lastLearningDate: data.lastLearningDate,
        lastCalculated: data.lastCalculated,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
      } as UserDashboardStats;
    } catch (error) {
      console.error('Error getting dashboard stats:', error);
      return null;
    }
  }

  /**
   * Subscribe to real-time updates for dashboard statistics
   */
  subscribeToDashboardStats(
    callback: (stats: UserDashboardStats | null) => void
  ): Unsubscribe | null {
    if (!auth.currentUser) {
      callback(null);
      return null;
    }

    const userId = auth.currentUser.uid;
    return subscribeToDocument<UserDashboardStats>('user_dashboard_stats', userId, callback);
  }

  /**
   * Calculate and update dashboard statistics
   */
  async calculateDashboardStats(): Promise<void> {
    try {
      if (!auth.currentUser) return;
      
      const userId = auth.currentUser.uid;
      
      // Get all of user's progress documents
      const progressQuery = query(
        collection(db, 'user_progress'),
        where('userId', '==', userId)
      );
      const progressSnapshot = await getDocs(progressQuery);

      // Get learning plan context so cards stay in sync with the visible course list
      const learningPlan = await this.getUserLearningPlan();
      const planCourseCount = learningPlan?.likedCourses.length ?? 0;
      const planCompletedCount = learningPlan
        ? learningPlan.likedCourses.filter(course => course.status === 'completed').length
        : 0;
      const planLearningTime = learningPlan
        ? learningPlan.likedCourses.reduce((total, course) => total + (course.timeSpent || 0), 0)
        : 0;
      const planLastAccessed = learningPlan?.lastAccessedAt ?? Timestamp.fromMillis(0);

      if (progressSnapshot.empty && planCourseCount === 0) {
        // Create default stats for users with no progress or courses in their plan
        const defaultStats: Omit<UserDashboardStats, 'id'> = {
          userId,
          coursesInProgress: 0,
          coursesCompleted: 0,
          totalCoursesEnrolled: 0,
          totalLearningTime: 0,
          thisWeekLearningTime: 0,
          thisMonthLearningTime: 0,
          currentStreak: 0,
          longestStreak: 0,
          lastLearningDate: Timestamp.now(),
          certificatesEarned: 0,
          badgesEarned: 0,
          lastCalculated: Timestamp.now(),
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        };

        await setDoc(doc(db, 'user_dashboard_stats', userId), defaultStats);
        return;
      }

      // Calculate statistics from progress data
      let coursesInProgress = 0;
      let coursesCompleted = 0;
      let totalLearningTime = 0;
      let lastLearningDate = planLastAccessed;

      progressSnapshot.docs.forEach(doc => {
        const progress = doc.data();
        if (progress.completedAt) {
          coursesCompleted++;
        } else if (progress.lastAccessedAt) {
          // Only count as in progress if it has been accessed (started)
          coursesInProgress++;
        }
        totalLearningTime += progress.totalTimeSpent || 0;
        if (progress.lastAccessedAt && progress.lastAccessedAt > lastLearningDate) {
          lastLearningDate = progress.lastAccessedAt;
        }
      });
      
      let totalCoursesEnrolled = progressSnapshot.size;

      // Reconcile progress stats with the learning plan so dashboard counts match the cards
      // For completed courses, we can trust the progress data + plan data
      coursesCompleted = Math.max(coursesCompleted, planCompletedCount);
      
      // For in-progress courses, we should ONLY count courses that have actually been started.
      // The previous logic was: coursesInProgress = Math.max(coursesInProgress, planInProgressCount);
      // But planInProgressCount includes ALL courses in the plan that are not completed, even if not started.
      // We want to exclude "not started" courses from "In Progress".
      
      // So we rely on the progressSnapshot count for 'coursesInProgress' which checks for 'lastAccessedAt'.
      // However, we also need to check the learning plan for any courses marked as 'active' that MIGHT have progress 
      // but maybe missing from user_progress collection (unlikely but possible if data is out of sync).
      // A safer bet for "In Progress" is strictly: Has Progress AND Not Completed.
      
      // Let's refine the count from the learning plan to only include those with progress > 0
      const planStartedButNotCompletedCount = learningPlan
        ? learningPlan.likedCourses.filter(course => course.status !== 'completed' && (course.progress || 0) > 0).length
        : 0;

      coursesInProgress = Math.max(coursesInProgress, planStartedButNotCompletedCount);

      totalLearningTime = Math.max(totalLearningTime, planLearningTime);
      totalCoursesEnrolled = Math.max(totalCoursesEnrolled, planCourseCount);
      if (planLastAccessed.toMillis() > 0 && planLastAccessed > lastLearningDate) {
        lastLearningDate = planLastAccessed;
      }
      
      // Get monthly learning time from progress service
      const monthlyStats = await progressService.getCurrentMonthLearningStats();
      const thisMonthLearningTime = monthlyStats?.monthlyTimeSpent || 0;

      // Placeholder for weekly time
      const thisWeekLearningTime = 0;
      
      // Calculate streak based on recent activity
      const currentStreak = await this.calculateCurrentStreak(userId);
      
      // Get existing stats directly to avoid infinite recursion with getDashboardStats()
      // which calls calculateDashboardStats() if stats don't exist
      const existingStatsDoc = await getDoc(doc(db, 'user_dashboard_stats', userId));
      const existingStats = existingStatsDoc.exists() ? existingStatsDoc.data() as UserDashboardStats : null;
      
      // For now, longest streak is just the current streak until we track it historically
      const longestStreak = Math.max(existingStats?.longestStreak || 0, currentStreak);
      
      const stats: Omit<UserDashboardStats, 'id'> = {
        userId,
        coursesInProgress,
        coursesCompleted,
        totalCoursesEnrolled,
        totalLearningTime,
        thisWeekLearningTime,
        thisMonthLearningTime,
        currentStreak,
        longestStreak,
        lastLearningDate: lastLearningDate.toMillis() > 0 ? lastLearningDate : Timestamp.now(),
        certificatesEarned: coursesCompleted,
        badgesEarned: Math.floor(coursesCompleted / 2),
        lastCalculated: Timestamp.now(),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
      
      await setDoc(doc(db, 'user_dashboard_stats', userId), stats, { merge: true });
      
    } catch (error) {
      console.error('Error calculating dashboard stats:', error);
      throw error;
    }
  }

  /**
   * Update dashboard statistics (lighter version for frequent updates)
   */
  async updateDashboardStats(): Promise<void> {
    try {
      if (!auth.currentUser) return;
      
      const userId = auth.currentUser.uid;
      const now = Timestamp.now();
      
      // Check if stats exist
      const statsDoc = await getDoc(doc(db, 'user_dashboard_stats', userId));
      
      if (!statsDoc.exists()) {
        await this.calculateDashboardStats();
        return;
      }
      
      // Update last learning date and calculation time
      await updateDoc(doc(db, 'user_dashboard_stats', userId), {
        lastLearningDate: now,
        lastCalculated: now,
        updatedAt: now,
      });
      
    } catch (error) {
      console.error('Error calculating dashboard stats:', error);
    }
  }

  /**
   * Calculate current learning streak
   */
  private async calculateCurrentStreak(_userId: string): Promise<number> {
    try {
      const completionDates = await progressService.getModuleCompletionDates();
      if (completionDates.length === 0) {
        return 0;
      }

      // Sort dates in descending order
      completionDates.sort((a, b) => b.toMillis() - a.toMillis());

      let currentStreak = 0;
      let lastDate = new Date();

      // Get unique completion days
      const uniqueDays = [...new Set(completionDates.map(ts => ts.toDate().toDateString()))];

      if (uniqueDays.length === 0) {
        return 0;
      }

      // Check if the most recent completion was today or yesterday
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);

      const mostRecentCompletion = new Date(uniqueDays[0]);

      if (
        mostRecentCompletion.toDateString() === today.toDateString() ||
        mostRecentCompletion.toDateString() === yesterday.toDateString()
      ) {
        currentStreak = 1;
        lastDate = mostRecentCompletion;

        for (let i = 1; i < uniqueDays.length; i++) {
          const currentDate = new Date(uniqueDays[i]);
          const diff = lastDate.getTime() - currentDate.getTime();
          const days_diff = Math.floor(diff / (1000 * 60 * 60 * 24));

          if (days_diff === 1) {
            currentStreak++;
          } else {
            break;
          }
          lastDate = currentDate;
        }
      }

      return currentStreak;
    } catch (error) {
      console.error('Error calculating streak:', error);
      return 0;
    }
  }

  /**
   * Check if course is in user's learning plan
   */
  async isCourseInLearningPlan(courseId: string): Promise<boolean> {
    try {
      const learningPlan = await this.getUserLearningPlan();
      if (!learningPlan) return false;
      
      return learningPlan.likedCourses.some(c => c.courseId === courseId);
    } catch (error) {
      console.error('Error checking if course is in learning plan:', error);
      return false;
    }
  }

  /**
   * Get courses from user's learning plan
   */
  async getLearningPlanCourses(): Promise<LikedCourse[]> {
    try {
      // Get all learning plans to aggregate courses
      const allPlans = await this.getAllUserLearningPlans();
      if (allPlans.length === 0) return [];

      const courseMap = new Map<string, LikedCourse>();

      // 1. Fetch AI Learning Plans for all UserLearningPlans that have a planId
      const planIds = allPlans.map(p => p.planId).filter((id): id is string => !!id);
      
      // Fetch AI plans to get recommended courses
      // Note: Collection name is 'learningPlans' in skillsAssessment.ts but 'learning_plans' was used here.
      // Checking skillsAssessment.ts, it uses 'learningPlans'.
      const aiPlanPromises = planIds.map(async (id) => {
        try {
          return await getDoc(doc(db, 'learningPlans', id));
        } catch (err) {
          console.warn(`Failed to load learning plan ${id}. It may have been deleted or you lack permissions.`, err);
          return null;
        }
      });
      
      const aiPlanSnapshotsRaw = await Promise.all(aiPlanPromises);
      const aiPlanSnapshots = aiPlanSnapshotsRaw.filter((snap): snap is import('firebase/firestore').DocumentSnapshot => snap !== null);
      
      // Process AI Plans (Recommended Courses)
      aiPlanSnapshots.forEach(snap => {
        if (snap.exists()) {
          const data = snap.data();
          const recommendedCourses = data.recommendedCourses as any[];
          if (recommendedCourses) {
            recommendedCourses.forEach(rc => {
              if (rc.status === 'active') {
                // Convert to LikedCourse format
                const course: LikedCourse = {
                  courseId: rc.courseId,
                  courseTitle: rc.courseTitle,
                  addedAt: data.generationDate || Timestamp.now(),
                  progress: 0,
                  timeSpent: 0,
                  priority: 3, // Default
                  source: 'recommendation',
                  status: 'active'
                };
                // Add to map if not exists
                if (!courseMap.has(course.courseId)) {
                   courseMap.set(course.courseId, course);
                }
              }
            });
          }
        }
      });

      // 2. Process User Learning Plans (Liked Courses) - Overwrite/Update AI courses
      // Sort plans oldest to newest
      const sortedPlans = allPlans.sort((a, b) => a.createdAt.toMillis() - b.createdAt.toMillis());

      for (const plan of sortedPlans) {
        for (const course of plan.likedCourses) {
          const existing = courseMap.get(course.courseId);
          if (existing) {
            // Merge logic: keep max progress
            courseMap.set(course.courseId, {
              ...course,
              progress: Math.max(existing.progress, course.progress),
              timeSpent: Math.max(existing.timeSpent, course.timeSpent),
              // If either is completed, mark as completed
              status: (existing.status === 'completed' || course.status === 'completed') ? 'completed' : course.status
            });
          } else {
            courseMap.set(course.courseId, course);
          }
        }
      }

      const aggregatedCourses = Array.from(courseMap.values());
      if (aggregatedCourses.length === 0) return [];

      // 3. Fetch progress from user_progress collection to ensure we have the latest
      const userId = auth.currentUser?.uid;
      if (!userId) return aggregatedCourses;

      const progressSnapshot = await getDocs(
        query(collection(db, 'user_progress'), where('userId', '==', userId))
      );

      const progressMap = new Map<string, UserProgress>();
      progressSnapshot.forEach(docSnap => {
        const data = { ...(docSnap.data() as UserProgress), id: docSnap.id };
        progressMap.set(data.courseId, data);
      });

      const sortedCourses = aggregatedCourses.sort((a, b) => {
        // Sort by priority (higher first), then by added date (newer first)
        if (a.priority !== b.priority) {
          return b.priority - a.priority;
        }
        return b.addedAt.toMillis() - a.addedAt.toMillis();
      });

      return sortedCourses.map(course => {
        const progress = progressMap.get(course.courseId);
        if (!progress) {
          return course;
        }

        const completion = Math.min(100, Math.max(0, Math.round(progress.completionPercentage ?? 0)));
        const completedAt = progress.completedAt || (completion >= 100 ? progress.updatedAt : course.completedAt);
        const startedAt = course.startedAt || progress.startedAt || progress.lastAccessedAt || progress.enrolledAt;
        const lastAccessedAt = progress.lastAccessedAt;
        const status: LikedCourse['status'] = completion >= 100 ? 'completed' : 'active';

        return {
          ...course,
          progress: completion,
          timeSpent: progress.totalTimeSpent ?? course.timeSpent,
          completedAt: completedAt ?? course.completedAt,
          startedAt: startedAt ?? course.startedAt,
          lastAccessedAt,
          status,
        };
      });
    } catch (error) {
      console.error('Error getting learning plan courses:', error);
      return [];
    }
  }

  /**
   * Get all user learning plans (active and archived)
   */
  async getAllUserLearningPlans(): Promise<UserLearningPlan[]> {
    try {
      if (!auth.currentUser) return [];
      
      const userId = auth.currentUser.uid;
      const q = query(
        collection(db, 'user_learning_plans'),
        where('userId', '==', userId)
      );
      
      const snapshot = await getDocs(q);
      if (snapshot.empty) return [];
      
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          lastAccessedAt: data.lastAccessedAt,
        } as UserLearningPlan;
      });
    } catch (error) {
      console.error('Error getting all user learning plans:', error);
      return [];
    }
  }
}

// Export singleton instance
export const learningPlansService = LearningPlansService.getInstance();

